// /working-schedules — Working schedules and public holidays (ops simulation T15)
//
// Odoo-style working schedules: a named working week (days, hours, zone) with
// public holidays, put on a store and inherited by its staff, overridable per
// person. A store with no schedule works its country's days (GCC Sun–Thu).
// Holidays are entered by hand. A no-show on a holiday is not marked absent.

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  Badge,
  Input,
  Skeleton,
  EmptyState,
  Modal,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { locationService } from '@/services/location.service';
import { userService } from '@/services/user.service';
import { DaysPill, hhmm } from '@/features/shifts';
import {
  useWorkingSchedules,
  useWorkingSchedule,
  useCreateWorkingSchedule,
  useUpdateWorkingSchedule,
  useDeleteWorkingSchedule,
  useAddHoliday,
  useRemoveHoliday,
  useAssignLocationSchedule,
  useAssignUserSchedule,
} from '@/features/working-schedules';
import type { WorkingSchedule } from '@/features/working-schedules';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const TIMEZONE_OPTIONS = [
  { value: '', label: "Store's timezone" },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dubai', label: 'UAE (GST)' },
  { value: 'Asia/Muscat', label: 'Oman (GST)' },
  { value: 'Asia/Riyadh', label: 'Saudi Arabia (AST)' },
  { value: 'Asia/Qatar', label: 'Qatar (AST)' },
  { value: 'Asia/Kuwait', label: 'Kuwait (AST)' },
  { value: 'Asia/Bahrain', label: 'Bahrain (AST)' },
];

/** "Fri, Sat" — the days a schedule is off, for the card summary. */
export function offDaysLabel(workDays: number[]): string {
  const off = DAY_LABELS.filter((_, idx) => !workDays.includes(idx));
  return off.length ? off.join(', ') : 'none';
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}

function WorkingSchedulesContent() {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? '');
  const canManage = ['OWNER', 'MANAGER', 'MODERATOR', 'ADMIN', 'SUPER_ADMIN'].includes(role);

  const { data: schedules, isLoading } = useWorkingSchedules();
  const list = useMemo<WorkingSchedule[]>(() => schedules ?? [], [schedules]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId = selectedId ?? list[0]?.id ?? null;
  const { data: detail, isLoading: detailLoading } = useWorkingSchedule(activeId);

  const createMutation = useCreateWorkingSchedule();
  const updateMutation = useUpdateWorkingSchedule();
  const deleteMutation = useDeleteWorkingSchedule();
  const addHoliday = useAddHoliday();
  const removeHoliday = useRemoveHoliday();
  const assignLocation = useAssignLocationSchedule();
  const assignUser = useAssignUserSchedule();

  const { data: locationsData } = useQuery({
    queryKey: ['working-schedule-locations-picker'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () => (locationsData?.data ?? []).map((l) => ({ value: l.id, label: `${l.name} — ${l.location_code}` })),
    [locationsData],
  );
  const { data: usersData } = useQuery({
    queryKey: ['working-schedule-users-picker', tenantId],
    queryFn: () => userService.list({ limit: 100, is_active: 'true', tenant_id: tenantId || undefined }),
    staleTime: 60_000,
  });
  const userOptions = useMemo(
    () => (usersData?.data ?? []).map((u) => ({ value: u.id, label: u.name })),
    [usersData],
  );

  // ─── Create / edit modal ───────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WorkingSchedule | null>(null);
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [timezone, setTimezone] = useState('');
  // null on create = let the server default from the country (GCC Sun–Thu).
  const [workDays, setWorkDays] = useState<number[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function openCreate() {
    setEditing(null);
    setName('');
    setStartTime('09:00');
    setEndTime('18:00');
    setTimezone('');
    setWorkDays(null);
    setModalOpen(true);
  }

  function openEdit(schedule: WorkingSchedule) {
    setEditing(schedule);
    setName(schedule.name);
    setStartTime(hhmm(schedule.start_time));
    setEndTime(hhmm(schedule.end_time));
    setTimezone(schedule.timezone ?? '');
    setWorkDays(schedule.work_days);
    setModalOpen(true);
  }

  function toggleDay(day: number) {
    setWorkDays((prev) => {
      const days = prev ?? [];
      return days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b);
    });
  }

  const handleSave = useCallback(() => {
    if (!name.trim()) { toast({ variant: 'warning', title: 'Name required' }); return; }
    if (!TIME_HHMM.test(startTime) || !TIME_HHMM.test(endTime)) {
      toast({ variant: 'warning', title: 'Enter valid hours', description: 'Use 24-hour HH:mm, e.g. 09:00' });
      return;
    }
    if (startTime === endTime) { toast({ variant: 'warning', title: 'Start and end must differ' }); return; }
    if (workDays !== null && workDays.length === 0) { toast({ variant: 'warning', title: 'Select at least one working day' }); return; }

    const done = (title: string) => ({
      onSuccess: () => { toast({ variant: 'success', title }); setModalOpen(false); },
      onError: (err: unknown) => { toast({ variant: 'error', title: 'Failed', description: errorText(err) }); },
    });
    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          input: { name: name.trim(), start_time: startTime, end_time: endTime, timezone: timezone || null, work_days: workDays ?? undefined },
        },
        done('Schedule updated'),
      );
    } else {
      createMutation.mutate(
        { name: name.trim(), start_time: startTime, end_time: endTime, timezone: timezone || undefined, work_days: workDays ?? undefined },
        {
          ...done('Schedule created'),
          onSuccess: (created) => { toast({ variant: 'success', title: 'Schedule created' }); setModalOpen(false); setSelectedId(created.id); },
        },
      );
    }
  }, [name, startTime, endTime, timezone, workDays, editing, createMutation, updateMutation, toast]);

  // ─── Holidays / assignments ────────────────────────────────────
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [assignLocationId, setAssignLocationId] = useState('');
  const [assignUserId, setAssignUserId] = useState('');

  function handleAddHoliday() {
    if (!activeId) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(holidayDate)) { toast({ variant: 'warning', title: 'Pick a date' }); return; }
    if (!holidayName.trim()) { toast({ variant: 'warning', title: 'Name the holiday' }); return; }
    addHoliday.mutate({ id: activeId, holiday_date: holidayDate, name: holidayName.trim() }, {
      onSuccess: () => { toast({ variant: 'success', title: 'Holiday added' }); setHolidayDate(''); setHolidayName(''); },
      onError: (err) => { toast({ variant: 'error', title: 'Failed', description: errorText(err) }); },
    });
  }

  function handleAssignLocation(locationId: string, scheduleId: string | null) {
    assignLocation.mutate({ locationId, scheduleId }, {
      onSuccess: () => { toast({ variant: 'success', title: scheduleId ? 'Store assigned' : 'Store back on its country default' }); setAssignLocationId(''); },
      onError: (err) => { toast({ variant: 'error', title: 'Failed', description: errorText(err) }); },
    });
  }

  function handleAssignUser(userId: string, scheduleId: string | null) {
    assignUser.mutate({ userId, scheduleId }, {
      onSuccess: () => { toast({ variant: 'success', title: scheduleId ? 'Override saved' : "Back on the store's schedule" }); setAssignUserId(''); },
      onError: (err) => { toast({ variant: 'error', title: 'Failed', description: errorText(err) }); },
    });
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-indigo-500 to-blue-500 p-6 sm:p-7 text-white mb-6 shadow-lg">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25">
              Scheduling
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Working Schedules</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Set the working week and public holidays for each store. Staff follow their store unless you give them their own.
              A store without a schedule works its country's days.
            </p>
          </div>
          {canManage ? (
            <Button variant="primary" onClick={openCreate} className="!bg-white !text-indigo-700 hover:!bg-white/90 !border-transparent">+ New Schedule</Button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton variant="rect" height={280} className="rounded-xl" />
          <Skeleton variant="rect" height={280} className="rounded-xl lg:col-span-2" />
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10">
          <EmptyState
            variant="page"
            title="No working schedules"
            description="Stores work their country's days until you add a schedule. Add one to set hours and public holidays."
            action={canManage ? { label: '+ New Schedule', onClick: openCreate } : undefined}
          />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Schedule list */}
          <div className="space-y-3">
            {list.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelectedId(s.id)}
                className={`w-full text-left rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-200 border animate-slide-up ${
                  s.id === activeId
                    ? 'bg-indigo-50 border-indigo-300 dark:bg-indigo-900/30 dark:border-indigo-600'
                    : 'bg-white border-neutral-100 dark:bg-neutral-800 dark:border-neutral-700 hover:bg-brand-50 dark:hover:bg-neutral-700/60'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-bold text-neutral-900 dark:text-white truncate">{s.name}</p>
                  <Badge variant={s.is_active ? 'success' : 'voided'} size="sm" dot>{s.is_active ? 'Active' : 'Inactive'}</Badge>
                </div>
                <div className="mt-2"><DaysPill days={s.work_days} /></div>
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                  {hhmm(s.start_time)}–{hhmm(s.end_time)} · off {offDaysLabel(s.work_days)}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                  <span className="rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 px-2 py-0.5">{s.location_count ?? 0} stores</span>
                  <span className="rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300 px-2 py-0.5">{s.user_count ?? 0} staff</span>
                  <span className="rounded-full bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300 px-2 py-0.5">{s.holiday_count ?? 0} holidays</span>
                </div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2 space-y-6">
            {detailLoading || !detail ? (
              <Skeleton variant="rect" height={320} className="rounded-xl" />
            ) : (
              <>
                <section className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-5 border border-neutral-100 dark:border-neutral-700">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-neutral-900 dark:text-white">{detail.name}</h2>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums">
                        {hhmm(detail.start_time)}–{hhmm(detail.end_time)} · {detail.timezone ?? "store's timezone"}
                      </p>
                      <div className="mt-2"><DaysPill days={detail.work_days} /></div>
                    </div>
                    {canManage ? (
                      <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => openEdit(detail)}>Edit</Button>
                        <Button variant="secondary" onClick={() => setConfirmDelete(true)} className="!text-danger-600">Delete</Button>
                      </div>
                    ) : null}
                  </div>
                </section>

                {/* Public holidays */}
                <section className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-5 border border-neutral-100 dark:border-neutral-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-warning-500 to-danger-500 flex items-center justify-center text-white text-sm font-bold">H</div>
                    <div>
                      <h3 className="font-bold text-neutral-900 dark:text-white">Public holidays</h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">No one is marked absent on these days.</p>
                    </div>
                  </div>
                  {canManage ? (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end mb-4">
                      <div className="sm:w-44"><Input label="Date" type="date" value={holidayDate} onChange={setHolidayDate} /></div>
                      <div className="flex-1"><Input label="Name" value={holidayName} onChange={(v) => setHolidayName(v.slice(0, 100))} placeholder="e.g. National Day" /></div>
                      <Button variant="primary" onClick={handleAddHoliday} loading={addHoliday.isPending}>Add</Button>
                    </div>
                  ) : null}
                  {detail.holidays.length === 0 ? (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">No holidays yet.</p>
                  ) : (
                    <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
                      {detail.holidays.map((h) => (
                        <li key={h.id} className="flex items-center justify-between py-2 even:bg-neutral-50 dark:even:bg-neutral-900/30 px-2 rounded hover:bg-brand-50 dark:hover:bg-neutral-700/60 transition-colors">
                          <span className="text-sm text-neutral-900 dark:text-white">
                            <span className="font-mono tabular-nums text-neutral-500 dark:text-neutral-400 mr-3">{h.holiday_date}</span>
                            {h.name}
                          </span>
                          {canManage ? (
                            <Button variant="secondary" className="text-xs px-2 py-1"
                              onClick={() => removeHoliday.mutate({ id: detail.id, holidayId: h.id })}>Remove</Button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* Stores + staff */}
                <div className="grid gap-6 md:grid-cols-2">
                  <section className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-5 border border-neutral-100 dark:border-neutral-700">
                    <h3 className="font-bold text-neutral-900 dark:text-white">Stores</h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">Staff at these stores work this week.</p>
                    {canManage ? (
                      <div className="flex gap-2 items-end mb-3">
                        <div className="flex-1">
                          <Select variant="searchable" options={locationOptions} value={assignLocationId}
                            onChange={(v) => setAssignLocationId(typeof v === 'string' ? v : '')} placeholder="Add a store…" />
                        </div>
                        <Button variant="primary" disabled={!assignLocationId} loading={assignLocation.isPending}
                          onClick={() => handleAssignLocation(assignLocationId, detail.id)}>Assign</Button>
                      </div>
                    ) : null}
                    {detail.locations.length === 0 ? (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">No stores yet.</p>
                    ) : (
                      <ul className="space-y-1">
                        {detail.locations.map((l) => (
                          <li key={l.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 bg-violet-50 dark:bg-violet-900/20">
                            <span className="text-sm text-violet-800 dark:text-violet-200">{l.name}</span>
                            {canManage ? (
                              <button type="button" className="text-xs text-danger-600 hover:underline"
                                onClick={() => handleAssignLocation(l.id, null)}>Remove</button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <section className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-5 border border-neutral-100 dark:border-neutral-700">
                    <h3 className="font-bold text-neutral-900 dark:text-white">Staff overrides</h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">These people work this week, whatever their store's schedule.</p>
                    {canManage ? (
                      <div className="flex gap-2 items-end mb-3">
                        <div className="flex-1">
                          <Select variant="searchable" options={userOptions} value={assignUserId}
                            onChange={(v) => setAssignUserId(typeof v === 'string' ? v : '')} placeholder="Add a person…" />
                        </div>
                        <Button variant="primary" disabled={!assignUserId} loading={assignUser.isPending}
                          onClick={() => handleAssignUser(assignUserId, detail.id)}>Assign</Button>
                      </div>
                    ) : null}
                    {detail.users.length === 0 ? (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">No overrides.</p>
                    ) : (
                      <ul className="space-y-1">
                        {detail.users.map((u) => (
                          <li key={u.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 bg-pink-50 dark:bg-pink-900/20">
                            <span className="text-sm text-pink-800 dark:text-pink-200">{u.name}</span>
                            {canManage ? (
                              <button type="button" className="text-xs text-danger-600 hover:underline"
                                onClick={() => handleAssignUser(u.id, null)}>Remove</button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create / edit */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Schedule' : 'New Schedule'} variant="wide">
        <div className="space-y-4">
          <Input label="Name" value={name} onChange={(v) => setName(v.slice(0, 100))} placeholder="e.g. Muscat 5-day week" required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Day starts (HH:mm)" value={startTime} onChange={setStartTime} placeholder="09:00" />
            <Input label="Day ends (HH:mm)" value={endTime} onChange={setEndTime} placeholder="18:00" />
          </div>
          <Select label="Timezone" options={TIMEZONE_OPTIONS} value={timezone} onChange={(v) => setTimezone(typeof v === 'string' ? v : '')} />
          <div>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Working days</p>
            {workDays === null ? (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                Using the country's days (Gulf: Sunday to Thursday; India: Monday to Friday). Tap a day to choose your own.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((label, idx) => (
                <button key={idx} type="button" onClick={() => toggleDay(idx)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 ring-1 ${
                    workDays?.includes(idx)
                      ? 'bg-indigo-500 text-white ring-indigo-600 shadow-sm'
                      : 'bg-neutral-100 dark:bg-neutral-700/60 text-neutral-700 dark:text-neutral-200 ring-neutral-200/60 dark:ring-neutral-600/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {editing ? (
            <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
              <input type="checkbox" checked={editing.is_active}
                onChange={(e) => updateMutation.mutate({ id: editing.id, input: { is_active: e.target.checked } }, {
                  onSuccess: (updated) => setEditing(updated),
                })} />
              Active (an inactive schedule is ignored; its stores use the country's days)
            </label>
          ) : null}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Save Changes' : 'Create Schedule'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (!activeId) return;
          deleteMutation.mutate(activeId, {
            onSuccess: () => { toast({ variant: 'success', title: 'Schedule deleted' }); setConfirmDelete(false); setSelectedId(null); },
            onError: (err) => { toast({ variant: 'error', title: 'Failed', description: errorText(err) }); },
          });
        }}
        title="Delete schedule"
        description="Its stores and staff go back to their country's working days, and its holidays are removed."
        variant="warning"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export function WorkingSchedulesPage() {
  return (
    <PageContainer title="Working Schedules" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Shifts', href: '/shifts' }, { label: 'Working Schedules' }]}>
      <ErrorBoundary resetKey="working-schedules">
        <WorkingSchedulesContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
